import { configure } from "axios-hooks";
import Axios from "axios";
import {
  persistRotatedSession,
  getAccessToken,
  getRefreshToken,
  clearLegacyRefreshToken,
  isAuthStorageLocked,
  lockAuthStorage,
  authContextFromToken,
  sameAuthContext,
} from "@utility/storage/auth";
import * as appSession from "@utility/storage/session";
import {
  setSessionHandlers,
  publishLogout,
  withSessionLock,
  markCompanyChanged,
} from "@utility/storage/browserSession";

export const baseURL =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_PROD_API_URL
    : process.env.REACT_APP_DEV_API_URL;

const axios = Axios.create({
  baseURL,
  withCredentials: true,
});

let cancelSource = Axios.CancelToken.source();
let companySwitchBlocked = false;

export function abortInFlight() {
  cancelSource.cancel("session-changed");
  cancelSource = Axios.CancelToken.source();
}

/** Drop in-flight company work and refuse new company requests until the switch settles. */
export function beginCompanySwitch() {
  companySwitchBlocked = true;
  abortInFlight();
}

export function endCompanySwitch() {
  companySwitchBlocked = false;
}

const isCompanySwitchUrl = (url = "") => url.includes("switch-company");

const authErrorCode = (error) => {
  const data = error?.response?.data;
  if (data && typeof data === "object") return data.code;
  return undefined;
};

const isRefreshUrl = (url = "") => url.includes("/user/refresh");

const isPublicAuthUrl = (url = "") =>
  isRefreshUrl(url) ||
  /\/user\/login(?:\/|$|\?)/.test(url) ||
  url.includes("/user/forgot-password") ||
  url.includes("/user/reset-password") ||
  url.includes("/user/logout") ||
  url.includes("/signup-session/");

let refreshInFlight = null;
let supersededRecovery = null;
let authEpoch = 0;

/** Hide protected UI while auth is recovered or cleared. Registered by the store. */
let gateAuth = () => {};

export function setAuthGate(gate) {
  gateAuth = gate;
}

const goToLogin = () => {
  if (window.location.pathname !== "/auth/login") {
    window.location.assign("/auth/login");
  }
};

const goHomeForNewCompany = () => {
  markCompanyChanged();
  abortInFlight();
  if (window.location.pathname !== "/") {
    window.location.assign("/");
  } else {
    window.location.reload();
  }
};

const clearAccessToken = () => {
  appSession.removeObject(appSession.AUTH_TOKEN_KEY);
};

setSessionHandlers({
  companyChanged: goHomeForNewCompany,
  logout: () => {
    lockAuthStorage();
    clearAccessToken();
    abortInFlight();
    goToLogin();
  },
});

const attachAccessToken = (config, jwt) => {
  if (!jwt) return config;
  if (config.headers && typeof config.headers.set === "function") {
    config.headers.set("Authorization", `Bearer ${jwt}`);
    config.headers.set("x-auth-session", "browser");
  } else {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${jwt}`,
      "x-auth-session": "browser",
    };
  }
  return config;
};

const attachBrowserHeader = (config) => {
  if (config.headers && typeof config.headers.set === "function") {
    config.headers.set("x-auth-session", "browser");
    config.headers.set("x-2fa-method", "totp");
  } else {
    config.headers = {
      ...config.headers,
      "x-auth-session": "browser",
      "x-2fa-method": "totp",
    };
  }
  return config;
};

const postRefresh = async () => {
  if (isAuthStorageLocked()) {
    throw new Error("LOGGED_OUT");
  }
  const epoch = authEpoch;
  const legacyRefresh = getRefreshToken();
  const { data } = await axios.post(
    "/user/refresh",
    legacyRefresh ? { refreshToken: legacyRefresh } : {},
    { _sessionControl: true }
  );
  if (epoch !== authEpoch || isAuthStorageLocked()) {
    throw new Error("LOGGED_OUT");
  }
  if (data?.jwt) {
    persistRotatedSession({ jwt: data.jwt });
  }
  clearLegacyRefreshToken();
  return data?.jwt || null;
};

const refreshSession = (fresh = false) => {
  if (fresh || !refreshInFlight) {
    const run = withSessionLock(async () => {
      try {
        return await postRefresh();
      } catch (error) {
        if (authErrorCode(error) === "REFRESH_IN_PROGRESS") {
          return postRefresh();
        }
        throw error;
      }
    }).finally(() => {
      if (refreshInFlight === run) refreshInFlight = null;
    });
    refreshInFlight = run;
  }
  return refreshInFlight;
};

export function invalidateAccessToken() {
  authEpoch += 1;
  clearAccessToken();
}

const discardAccessToken = invalidateAccessToken;

/**
 * One recovery for every SESSION_SUPERSEDED in this page.
 * Other failures wait on this promise. They must not log the user out on their own.
 * The failed request is not sent again, so an old company mutation is not replayed.
 */
const recoverSupersededSession = () => {
  if (supersededRecovery) return supersededRecovery;

  gateAuth("UNKNOWN");
  discardAccessToken();
  abortInFlight();
  supersededRecovery = (async () => {
    try {
      const jwt = await refreshSession(true);
      if (jwt) {
        goHomeForNewCompany();
        return;
      }
      gateAuth("ERROR");
    } catch (refreshError) {
      if (refreshError?.message === "LOGGED_OUT") {
        gateAuth("UNAUTHENTICATED");
        goToLogin();
        return;
      }
      if (refreshError?.response?.status === 401) {
        discardAccessToken();
        gateAuth("UNAUTHENTICATED");
        publishLogout();
        goToLogin();
        return;
      }
      gateAuth("ERROR");
    }
  })().finally(() => {
    supersededRecovery = null;
  });

  return supersededRecovery;
};

axios.interceptors.request.use(
  async (config) => {
    const url = config.url || "";
    attachBrowserHeader(config);
    if (
      companySwitchBlocked &&
      !config._sessionControl &&
      !isCompanySwitchUrl(url) &&
      !isPublicAuthUrl(url)
    ) {
      return Promise.reject(new Axios.Cancel("company-switch"));
    }
    if (!config._sessionControl && !config.cancelToken) {
      config.cancelToken = cancelSource.token;
    }
    if (isPublicAuthUrl(url)) {
      if (url.includes("/user/logout")) {
        return attachAccessToken(config, getAccessToken());
      }
      return config;
    }

    let jwt = getAccessToken();
    if (!jwt) {
      try {
        jwt = await refreshSession();
      } catch {
        jwt = null;
      }
    }

    config._authContext = authContextFromToken(jwt);
    return attachAccessToken(config, jwt);
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (Axios.isCancel(error)) return Promise.reject(error);
    const status = error?.response?.status;
    const originalRequest = error?.config;
    const url = originalRequest?.url || "";
    const message = error?.response?.data?.message;
    const isRefreshCall = isRefreshUrl(url);
    const missingToken =
      status === 400 &&
      (message === "Token missing" || message === "Token required!");

    if (status !== 401 && !missingToken) {
      return Promise.reject(error);
    }

    const code = authErrorCode(error);

    if (code === "SESSION_SUPERSEDED" && !isRefreshCall) {
      recoverSupersededSession();
      return Promise.reject(error);
    }

    if (isRefreshCall) {
      return Promise.reject(error);
    }

    if (code === "INVALID_TOKEN") {
      discardAccessToken();
      gateAuth("UNAUTHENTICATED");
      goToLogin();
      return Promise.reject(error);
    }

    const shouldRefresh =
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest._sessionControl &&
      (code === "TOKEN_EXPIRED" || missingToken);

    if (shouldRefresh) {
      originalRequest._retry = true;
      try {
        const jwt = await refreshSession();
        const sameCompany =
          code !== "TOKEN_EXPIRED" ||
          sameAuthContext(
            originalRequest._authContext,
            authContextFromToken(jwt)
          );
        if (!sameCompany) {
          if (jwt) goHomeForNewCompany();
          else goToLogin();
          return Promise.reject(error);
        }
        attachAccessToken(originalRequest, jwt);
        return axios(originalRequest);
      } catch (refreshError) {
        if (refreshError?.response?.status === 401) {
          discardAccessToken();
          gateAuth("UNAUTHENTICATED");
          publishLogout();
          goToLogin();
        } else if (refreshError?.message !== "LOGGED_OUT") {
          gateAuth("ERROR");
        }
        return Promise.reject(refreshError);
      }
    }

    if (window.location.pathname !== "/auth/login" && !getAccessToken()) {
      publishLogout();
      goToLogin();
    }

    return Promise.reject(error);
  }
);

configure({ axios });

export default axios;
export { refreshSession, publishLogout as broadcastLogout };
export function forceLogout() {
  publishLogout();
  goToLogin();
}
